import { ZonedDatePipe } from '@awork/_shared/pipes/zoned-date.pipe'
import { A11yModule, CdkTrapFocus } from '@angular/cdk/a11y'
import { DOCUMENT } from '@angular/common'
import type { AfterViewInit, ElementRef, OnDestroy, OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  InjectionToken,
  afterRenderEffect,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core'
import type { SafeResourceUrl } from '@angular/platform-browser'
import { DomSanitizer } from '@angular/platform-browser'
import type { Observable } from 'rxjs'
import { EMPTY, Subscription, defer, finalize, from, map, of, switchMap, take, tap } from 'rxjs'
import { BadgeComponent } from '../../data-display/badge/badge.component'
import { ActionButtonComponent } from '../../buttons/action-button/action-button.component'
import { DismissButtonComponent } from '../../icon-buttons/dismiss-button/dismiss-button.component'
import { IconButtonComponent } from '../../icon-buttons/icon-button/icon-button.component'
import { ActionBarLayoutComponent } from '../../layout/action-bar-layout/action-bar-layout.component'
import { OverlayComponent } from '../../layout/overlay/overlay.component'
import { DotsLoaderComponent } from '../../ui-help/dots-loader/dots-loader.component'
import { ModalService } from '../../../services/modal-service/modal.service'
import { ToastService } from '../../../services/toast-service/toast.service'
import { FileSizePipe } from '../../../pipes/file-size/file-size.pipe'
import { copyToClipboard } from '../../../functions/copy-clipboard'
import { DesktopService } from '@awork/_shared/services/desktop-service/desktop.service'
import { ZoomService } from '@awork/_shared/services/zoom-service/zoom.service'
import { createResizeObserver } from '@awork/_shared/functions/resize-observable'
import { Color } from '@awork/_shared/types/color'
import { Size } from '@awork/_shared/types/size'
import { FilePreviewLightboxThumbnailComponent } from './file-preview-lightbox-thumbnail/file-preview-lightbox-thumbnail.component'
import type { FilePreviewImageDimensions, FilePreviewItem, FilePreviewLightboxRequest } from './types'
import {
  TEXT_FILE_SYNTAX_HIGHLIGHT_MAX_SIZE_BYTES,
  getTextFilePreviewSyntaxLanguage
} from '@awork/_shared/functions/text-file-preview'
import { createSafeHtmlPreviewDocument } from '@awork/_shared/functions/html-file-preview'
import { getTextFilePreviewHighlightedHtml } from './text-file-preview-highlight'

const PREVIEW_TRANSITION_DURATION_MS = 200
const DOCUMENT_ESCAPE_LISTENER_OPTIONS: AddEventListenerOptions = { capture: true, passive: false }
type FilePreviewCollectionState = 'loading' | 'ready' | 'error'
interface LoadedTextPreview {
  readonly content: string
  readonly blobSizeBytes: number
}

export type FilePreviewResizeObserverFactory = (callback: ResizeObserverCallback) => ResizeObserver

export const FILE_PREVIEW_RESIZE_OBSERVER_FACTORY = new InjectionToken<FilePreviewResizeObserverFactory>(
  'FilePreviewResizeObserverFactory',
  { factory: () => createResizeObserver }
)

@Component({
  selector: 'aw-file-preview-lightbox',
  templateUrl: './file-preview-lightbox.component.html',
  styleUrls: ['./file-preview-lightbox.component.scss'],
  imports: [
    A11yModule,
    ActionBarLayoutComponent,
    ActionButtonComponent,
    BadgeComponent,
    ZonedDatePipe,
    DismissButtonComponent,
    DotsLoaderComponent,
    FilePreviewLightboxThumbnailComponent,
    FileSizePipe,
    IconButtonComponent,
    OverlayComponent
  ],
  providers: [ZoomService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'handleWindowResize()',
    '(window:beforeunload)': 'handleUnload($event)',
    '(document:keydown)': 'handleKeyboardEvent($event)',
    '(document:mousemove)': 'handleMouseMove($event)',
    '(document:mouseup)': 'handleMouseUp($event)',
    '(document:touchmove)': 'handleTouchMove($event)',
    '(document:touchend)': 'handleTouchEnd($event)'
  }
})
export class FilePreviewLightboxComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly desktopService = inject(DesktopService)
  private readonly document = inject(DOCUMENT)
  private readonly modalService = inject(ModalService)
  private readonly sanitizer = inject(DomSanitizer)
  private readonly toastService = inject(ToastService)
  private readonly zoomService = inject(ZoomService)
  private readonly createTextPreviewResizeObserver = inject(FILE_PREVIEW_RESIZE_OBSERVER_FACTORY)

  readonly request = input.required<FilePreviewLightboxRequest>()
  readonly hiding = output<void>()

  readonly overlay = viewChild<OverlayComponent>('overlay')
  private readonly focusTrap = viewChild(CdkTrapFocus)
  private readonly imagePreview = viewChild<ElementRef<HTMLElement>>('imagePreview')
  private readonly previewImage = viewChild<ElementRef<HTMLImageElement>>('previewImage')
  private readonly textPreviewContainer = viewChild<ElementRef<HTMLElement>>('textPreviewContainer')
  private readonly textPreviewContent = viewChild<ElementRef<HTMLElement>>('textPreviewContent')
  private readonly thumbnailsDetails = viewChild<ElementRef<HTMLElement>>('thumbnailsDetails')
  private readonly handleDocumentKeydownCapture = (event: KeyboardEvent): void => this.handleCapturedEscape(event)
  private readonly modalStackDepthAtOpen = this.modalService.modals.length

  protected readonly items = signal<readonly FilePreviewItem[]>([])
  protected readonly collectionState = signal<FilePreviewCollectionState>('loading')
  protected readonly selectedIndex = signal(0)
  protected readonly currentFile = computed(() => this.items()[this.selectedIndex()] ?? null)
  protected readonly showGallery = computed(() => this.request().showGallery)
  protected readonly previewUrl = signal<string | null>(null)
  protected readonly safePreviewResourceUrl = signal<SafeResourceUrl | null>(null)
  private readonly loadedTextPreview = signal<LoadedTextPreview | null>(null)
  protected readonly textContent = computed(() => this.loadedTextPreview()?.content ?? null)
  protected readonly highlightedTextContent = computed(() => this.getHighlightedTextContent())
  protected readonly isTextContentReady = computed(() => this.textContent() !== null)
  private readonly hasUnwrappedTextOverflow = signal(false)
  private readonly textWrappingPreference = signal<boolean | null>(null)
  protected readonly isTextWrapped = computed(
    () => this.hasUnwrappedTextOverflow() && (this.textWrappingPreference() ?? true)
  )
  protected readonly canToggleTextWrapping = computed(
    () => this.isTextContentReady() && this.hasUnwrappedTextOverflow()
  )
  protected readonly copyLoading = signal(false)
  protected readonly isLoading = signal(true)
  protected readonly previewError = signal<'load-failed' | 'preview-failed' | 'too-large' | null>(null)
  protected readonly downloadFailed = signal(false)
  protected readonly downloadLoading = signal(false)
  protected readonly deleteLoading = signal(false)
  protected readonly videoLoading = signal(true)
  protected readonly previewActive = signal(true)
  protected readonly isTransitioning = signal(false)
  protected readonly transitionDirection = signal<'next' | 'prev' | 'none'>('none')
  protected readonly imageDimensions = signal<FilePreviewImageDimensions | null>(null)
  protected readonly zoomFactor = computed(() => this.zoomService.zoomFactor())
  protected readonly panOffset = computed(() => this.zoomService.panOffset())
  protected readonly fitScale = signal(1)
  protected readonly zoomInActive = computed(() => this.zoomFactor() < ZoomService.MAX_ZOOM)
  protected readonly zoomOutActive = computed(() => this.zoomFactor() > ZoomService.MIN_ZOOM)
  protected readonly isPannable = computed(() => this.zoomFactor() > this.fitScale())
  protected readonly isDragging = signal(false)

  protected readonly Size = Size
  protected readonly Color = Color
  protected readonly translations = q.translations.FilePreviewLightboxComponent
  protected readonly textWrapTooltip = computed(() =>
    this.isTextContentReady() && !this.isTextWrapped() && !this.hasUnwrappedTextOverflow()
      ? this.translations.allLinesFit
      : this.translations.wrapLines
  )
  protected readonly commonTranslations = q.translations.common
  protected readonly activityLogTranslations = q.translations.ActivityLogComponent

  private itemsSubscription: Subscription | null = null
  private previewSubscription: Subscription | null = null
  private readonly actionSubscriptions = new Subscription()
  private ownedPreviewUrl: string | null = null
  private transitionTimer: ReturnType<typeof setTimeout> | null = null
  private readonly textPreviewResizeObserver = this.createTextPreviewResizeObserver(() => this.updateTextOverflow())
  private lastPanPosition = { x: 0, y: 0 }
  private hasClosed = false

  constructor() {
    effect(() => {
      const textPreviewContainer = this.textPreviewContainer()?.nativeElement

      this.textPreviewResizeObserver.disconnect()

      if (textPreviewContainer) {
        this.textPreviewResizeObserver.observe(textPreviewContainer)
      }
    })
    afterRenderEffect(() => this.updateTextOverflow())
    afterRenderEffect(onCleanup => this.decodeImagePreview(onCleanup))
  }

  /**
   * Subscribes to the file collection and registers keyboard handling.
   */
  ngOnInit(): void {
    this.document.addEventListener('keydown', this.handleDocumentKeydownCapture, DOCUMENT_ESCAPE_LISTENER_OPTIONS)
    this.itemsSubscription = this.request().items.subscribe({
      next: items => {
        this.collectionState.set('ready')
        this.applyItems(items)
      },
      error: () => this.showCollectionError(),
      complete: () => {
        if (this.collectionState() === 'loading') {
          this.showCollectionError()
        }
      }
    })
  }

  /**
   * Shows the overlay and focuses the initial control.
   */
  ngAfterViewInit(): void {
    this.overlay()?.show()
    this.focusTrap()?.focusTrap.focusInitialElementWhenReady({ preventScroll: true })
  }

  /**
   * Releases subscriptions, preview resources, and event listeners.
   */
  ngOnDestroy(): void {
    this.document.removeEventListener('keydown', this.handleDocumentKeydownCapture, DOCUMENT_ESCAPE_LISTENER_OPTIONS)
    this.itemsSubscription?.unsubscribe()
    this.previewSubscription?.unsubscribe()
    this.actionSubscriptions.unsubscribe()
    this.textPreviewResizeObserver.disconnect()
    this.releaseOwnedPreviewUrl()

    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer)
    }
  }

  /**
   * Closes the lightbox through its shared overlay.
   */
  close(): void {
    const overlay = this.overlay()

    if (overlay?.visible()) {
      overlay.hide()

      return
    }

    this.finishClosing()
  }

  /**
   * Finishes cleanup when the overlay starts hiding.
   */
  protected onOverlayHiding(): void {
    this.finishClosing()
  }

  /**
   * Selects one gallery item.
   * @param {number} index Item index in the previewable collection.
   */
  protected selectThumbnail(index: number): void {
    this.selectIndex(index, 'none')
  }

  /**
   * Selects the next previewable item.
   */
  protected next(): void {
    this.selectIndex(this.selectedIndex() + 1, 'next')
  }

  /**
   * Selects the previous previewable item.
   */
  protected prev(): void {
    this.selectIndex(this.selectedIndex() - 1, 'prev')
  }

  /**
   * Stores an explicit soft-wrapping preference for the selected file.
   */
  protected toggleTextWrapping(): void {
    if (!this.canToggleTextWrapping()) {
      return
    }

    this.textWrappingPreference.set(!this.isTextWrapped())
  }

  /**
   * Copies the complete decoded text preview to the clipboard.
   */
  protected copyTextContent(): void {
    const content = this.textContent()

    if (content === null || this.copyLoading()) {
      return
    }

    this.copyLoading.set(true)
    this.actionSubscriptions.add(
      defer(() => copyToClipboard(content))
        .pipe(
          take(1),
          finalize(() => this.copyLoading.set(false))
        )
        .subscribe({
          next: () => this.toastService.show(this.translations.textCopied, { type: 'confirmation' }),
          error: () => undefined
        })
    )
  }

  /**
   * Downloads the selected item through its source adapter.
   */
  protected downloadCurrentFile(): void {
    const currentFile = this.currentFile()

    if (!currentFile || this.downloadLoading()) {
      return
    }

    this.downloadFailed.set(false)
    this.downloadLoading.set(true)
    this.actionSubscriptions.add(
      currentFile
        .download()
        .pipe(take(1))
        .subscribe({
          error: () => {
            this.downloadFailed.set(true)
            this.downloadLoading.set(false)
          },
          complete: () => this.downloadLoading.set(false)
        })
    )
  }

  /**
   * Deletes the selected attachment through its optional source action.
   */
  protected deleteCurrentFile(): void {
    const deleteFile = this.currentFile()?.delete

    if (!deleteFile || this.deleteLoading()) {
      return
    }

    this.deleteLoading.set(true)
    this.actionSubscriptions.add(
      deleteFile()
        .pipe(take(1))
        .subscribe({
          error: () => this.deleteLoading.set(false),
          complete: () => this.deleteLoading.set(false)
        })
    )
  }

  /**
   * Sends selected local files through the attachment adapter.
   * @param {Event} event Native file input change event.
   */
  protected addNewFiles(event: Event): void {
    const fileInput = event.target as HTMLInputElement
    const files = Array.from(fileInput.files ?? [])
    const addFiles = this.request().addFiles

    if (!files.length || !addFiles) {
      return
    }

    this.actionSubscriptions.add(addFiles(files).subscribe({ error: () => undefined }))
    fileInput.value = ''
  }

  /**
   * Increases the selected image scale.
   */
  protected zoomIn(): void {
    if (!this.zoomInActive()) {
      return
    }

    this.zoomService.zoomIn()
    this.constrainPanOffset()
  }

  /**
   * Decreases the selected image scale.
   */
  protected zoomOut(): void {
    if (!this.zoomOutActive()) {
      return
    }

    this.zoomService.zoomOut()
    this.constrainPanOffset()
  }

  /**
   * Fits the selected image into the viewport.
   */
  protected scaleToFit(): void {
    this.updateFitScale()
    this.zoomService.setZoom(this.fitScale())
    this.constrainPanOffset()
  }

  /**
   * Records a native renderer failure.
   */
  protected onPreviewFailed(): void {
    this.previewError.set('preview-failed')
    this.isLoading.set(false)
  }

  /**
   * Marks video content as ready.
   */
  protected onVideoLoadedData(): void {
    this.videoLoading.set(false)
  }

  /**
   * Marks video content as loading.
   */
  protected onVideoLoadStart(): void {
    this.videoLoading.set(true)
  }

  /**
   * Starts mouse-based image panning.
   * @param {MouseEvent} event Pointer start event.
   */
  protected onPanStart(event: MouseEvent): void {
    if (!this.isPannable() || this.currentFile()?.previewKind !== 'image') {
      return
    }

    this.isDragging.set(true)
    this.lastPanPosition = { x: event.clientX, y: event.clientY }
    event.preventDefault()
  }

  /**
   * Starts touch-based image panning.
   * @param {TouchEvent} event Touch start event.
   */
  protected onTouchStart(event: TouchEvent): void {
    if (!this.isPannable() || event.touches.length !== 1 || this.currentFile()?.previewKind !== 'image') {
      return
    }

    this.isDragging.set(true)
    this.lastPanPosition = { x: event.touches[0].clientX, y: event.touches[0].clientY }
    this.preventTouchDefault(event)
  }

  /**
   * Keeps fitted images fitted when the viewport size changes.
   */
  handleWindowResize(): void {
    if (this.currentFile()?.previewKind !== 'image') {
      return
    }

    const previousFitScale = this.fitScale()
    const wasFitted = Math.abs(this.zoomFactor() - previousFitScale) < 0.01

    this.updateFitScale()

    if (wasFitted) {
      this.zoomService.setZoom(this.fitScale())
      this.constrainPanOffset()
    }
  }

  /**
   * Prevents links inside a PDF from navigating the Electron shell.
   * @param {BeforeUnloadEvent} event Window unload event.
   */
  handleUnload(event: BeforeUnloadEvent): void {
    const activeElement = this.document.activeElement as HTMLButtonElement | HTMLInputElement | null

    if (this.desktopService.isDesktop && activeElement?.type !== 'button') {
      event.returnValue = 'Navigation away from the desktop app is not allowed'
    }
  }

  /**
   * Handles gallery arrow navigation.
   * @param {Event} event Document keyboard event.
   */
  handleKeyboardEvent(event: Event): void {
    if (!(event instanceof KeyboardEvent) || !this.showGallery() || this.isNativeArrowKeyTarget(event)) {
      return
    }

    if (event.key === 'ArrowRight') {
      this.next()
    } else if (event.key === 'ArrowLeft') {
      this.prev()
    }
  }

  /**
   * Continues active mouse panning.
   * @param {Event} event Document mouse event.
   */
  handleMouseMove(event: Event): void {
    if (!this.isDragging() || !this.isPannable()) {
      return
    }

    const mouseEvent = event as MouseEvent

    this.panBy(mouseEvent.clientX, mouseEvent.clientY)
    mouseEvent.preventDefault()
  }

  /**
   * Stops active mouse panning.
   * @param {Event} event Document mouse event.
   */
  handleMouseUp(event: Event): void {
    if (!this.isDragging()) {
      return
    }

    this.isDragging.set(false)
    event.preventDefault()
  }

  /**
   * Continues active touch panning.
   * @param {Event} event Document touch event.
   */
  handleTouchMove(event: Event): void {
    const touchEvent = event as TouchEvent

    if (!this.isDragging() || !this.isPannable() || touchEvent.touches.length !== 1) {
      return
    }

    this.panBy(touchEvent.touches[0].clientX, touchEvent.touches[0].clientY)
    this.preventTouchDefault(touchEvent)
  }

  /**
   * Stops active touch panning.
   * @param {Event} event Document touch event.
   */
  handleTouchEnd(event: Event): void {
    if (!this.isDragging()) {
      return
    }

    this.isDragging.set(false)
    this.preventTouchDefault(event as TouchEvent)
  }

  /**
   * Checks whether an interactive control owns the arrow key event.
   * @param {KeyboardEvent} event Document keyboard event.
   * @returns {boolean} True when gallery navigation must preserve native behavior.
   */
  private isNativeArrowKeyTarget(event: KeyboardEvent): boolean {
    return event.composedPath().some(target => {
      if (
        target instanceof HTMLMediaElement ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return true
      }

      return (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.classList.contains('file-preview-lightbox__text-content'))
      )
    })
  }

  /**
   * Reconciles a new source collection with the current selection.
   * @param {readonly FilePreviewItem[]} items Latest previewable items.
   */
  private applyItems(items: readonly FilePreviewItem[]): void {
    if (!items.length) {
      this.items.set([])
      this.close()

      return
    }

    const currentFile = this.currentFile()
    const currentId = currentFile?.id
    const preferredId =
      currentId && items.some(item => item.id === currentId) ? currentId : this.request().initialItemId
    const nextIndex = Math.max(
      0,
      items.findIndex(item => item.id === preferredId)
    )
    const nextFile = items[nextIndex]
    const previewResourceChanged = !currentFile || !this.hasSamePreviewResource(currentFile, nextFile)

    this.items.set([...items])
    this.selectedIndex.set(nextIndex)

    const hasLoadedPreview = nextFile.previewKind === 'text' ? this.textContent() !== null : !!this.previewUrl()

    if (previewResourceChanged || (!hasLoadedPreview && !this.isLoading())) {
      this.loadCurrentPreview()
    }
  }

  /**
   * Checks whether a live item update still points to the selected preview resource.
   * @param {FilePreviewItem} currentFile Currently rendered preview item.
   * @param {FilePreviewItem} nextFile Updated item with the same selection position.
   * @returns {boolean} True when the renderer can keep its current resource and state.
   */
  private hasSamePreviewResource(currentFile: FilePreviewItem, nextFile: FilePreviewItem): boolean {
    const hasResourceId = currentFile.previewResourceId !== undefined || nextFile.previewResourceId !== undefined
    const hasSameLazyResource = hasResourceId
      ? currentFile.previewResourceId === nextFile.previewResourceId
      : currentFile.loadPreview === nextFile.loadPreview

    return (
      currentFile.id === nextFile.id &&
      currentFile.previewKind === nextFile.previewKind &&
      currentFile.mimeType === nextFile.mimeType &&
      currentFile.previewUrl === nextFile.previewUrl &&
      hasSameLazyResource &&
      currentFile.maxPreviewSizeBytes === nextFile.maxPreviewSizeBytes &&
      currentFile.imageDimensions?.width === nextFile.imageDimensions?.width &&
      currentFile.imageDimensions?.height === nextFile.imageDimensions?.height
    )
  }

  /**
   * Replaces unavailable collection content with an in-lightbox error state.
   */
  private showCollectionError(): void {
    this.collectionState.set('error')
    this.items.set([])
    this.selectedIndex.set(0)
    this.loadCurrentPreview()
  }

  /**
   * Changes selection and starts its preview transition.
   * @param {number} index Target collection index.
   * @param {'next' | 'prev' | 'none'} direction Navigation direction for visual feedback.
   */
  private selectIndex(index: number, direction: 'next' | 'prev' | 'none'): void {
    if (index < 0 || index >= this.items().length || index === this.selectedIndex()) {
      return
    }

    this.previewActive.set(false)
    this.transitionDirection.set(direction)
    this.isTransitioning.set(direction !== 'none')
    this.selectedIndex.set(index)
    this.loadCurrentPreview()
    this.previewActive.set(true)
    queueMicrotask(() => this.scrollSelectedThumbnailIntoView())

    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer)
    }

    this.transitionTimer = setTimeout(() => {
      this.isTransitioning.set(false)
      this.transitionDirection.set('none')
    }, PREVIEW_TRANSITION_DURATION_MS)
  }

  /**
   * Loads or selects the current item's media resource.
   */
  private loadCurrentPreview(): void {
    this.previewSubscription?.unsubscribe()
    this.previewSubscription = null
    this.releaseOwnedPreviewUrl()
    this.previewUrl.set(null)
    this.safePreviewResourceUrl.set(null)
    this.loadedTextPreview.set(null)
    this.hasUnwrappedTextOverflow.set(false)
    this.textWrappingPreference.set(null)
    this.previewError.set(null)
    this.downloadFailed.set(false)
    this.videoLoading.set(true)
    this.imageDimensions.set(this.currentFile()?.imageDimensions ?? null)
    this.zoomService.resetZoom()

    const currentFile = this.currentFile()

    if (!currentFile) {
      this.isLoading.set(false)

      return
    }

    if (
      currentFile.maxPreviewSizeBytes !== undefined &&
      currentFile.sizeBytes !== undefined &&
      currentFile.sizeBytes > currentFile.maxPreviewSizeBytes
    ) {
      this.previewError.set('too-large')
      this.isLoading.set(false)

      return
    }

    if (currentFile.previewUrl) {
      this.setPreviewUrl(currentFile.previewUrl)

      return
    }

    if (!currentFile.loadPreview) {
      this.previewError.set('load-failed')
      this.isLoading.set(false)

      return
    }

    this.isLoading.set(true)
    this.previewSubscription = currentFile
      .loadPreview()
      .pipe(
        take(1),
        switchMap(blob => this.useLoadedBlob(currentFile, blob))
      )
      .subscribe({
        error: () => {
          this.previewError.set('load-failed')
          this.isLoading.set(false)
        }
      })
  }

  /**
   * Validates and prepares one loaded preview Blob.
   * @param {FilePreviewItem} item
   * @param {Blob} blob
   * @returns {Observable<void>} Completes after the renderer content is ready.
   */
  private useLoadedBlob(item: FilePreviewItem, blob: Blob): Observable<void> {
    if (item.id !== this.currentFile()?.id) {
      return EMPTY
    }

    if (item.maxPreviewSizeBytes !== undefined && blob.size > item.maxPreviewSizeBytes) {
      this.previewError.set('too-large')
      this.isLoading.set(false)

      return EMPTY
    }

    if (item.previewKind === 'text') {
      return from(blob.text()).pipe(
        tap(content => this.setTextContent(item, content, blob.size)),
        map(() => undefined)
      )
    }

    if (item.previewKind === 'html') {
      return from(blob.text()).pipe(
        tap(content => this.setHtmlContent(item, content)),
        map(() => undefined)
      )
    }

    const rendererBlob = blob.slice(0, blob.size, item.mimeType)

    this.ownedPreviewUrl = URL.createObjectURL(rendererBlob)
    this.setPreviewUrl(this.ownedPreviewUrl)

    return of(undefined)
  }

  /**
   * Wraps uploaded HTML in a restrictive document policy before rendering it.
   * @param {FilePreviewItem} item
   * @param {string} content Decoded HTML file contents.
   */
  private setHtmlContent(item: FilePreviewItem, content: string): void {
    if (item.id !== this.currentFile()?.id) {
      return
    }

    const rendererBlob = new Blob([createSafeHtmlPreviewDocument(content)], { type: 'text/html' })

    this.ownedPreviewUrl = URL.createObjectURL(rendererBlob)
    this.setPreviewUrl(this.ownedPreviewUrl)
  }

  /**
   * Builds highlighted HTML for the selected eligible text preview.
   * @returns {string | null} Restricted highlighted HTML, or null for plain text.
   * @withSignal {Signal<LoadedTextPreview | null>} loadedTextPreview
   * @withSignal {Signal<FilePreviewItem | null>} currentFile
   */
  private getHighlightedTextContent(): string | null {
    const loadedTextPreview = this.loadedTextPreview()
    const currentFile = this.currentFile()

    if (!loadedTextPreview || !currentFile) {
      return null
    }

    const language = getTextFilePreviewSyntaxLanguage(currentFile.name, currentFile.mimeType)

    if (!language || loadedTextPreview.blobSizeBytes > TEXT_FILE_SYNTAX_HIGHLIGHT_MAX_SIZE_BYTES) {
      return null
    }

    return getTextFilePreviewHighlightedHtml(loadedTextPreview.content, language, loadedTextPreview.blobSizeBytes)
  }

  /**
   * Publishes decoded text when the requested item is still selected.
   * @param {FilePreviewItem} item
   * @param {string} content
   * @param {number} blobSizeBytes Loaded Blob size.
   */
  private setTextContent(item: FilePreviewItem, content: string, blobSizeBytes: number): void {
    if (item.id !== this.currentFile()?.id) {
      return
    }

    if (content.includes('\u0000')) {
      throw new Error('Text preview contains binary content')
    }

    this.loadedTextPreview.set({ content, blobSizeBytes })
    this.isLoading.set(false)
  }

  /**
   * Measures whether the unwrapped text exceeds its visible container.
   */
  private updateTextOverflow(): void {
    const content = this.textContent()
    const container = this.textPreviewContainer()?.nativeElement
    const textElement = this.textPreviewContent()?.nativeElement

    if (content === null || !container || !textElement) {
      this.hasUnwrappedTextOverflow.set(false)

      return
    }

    const wrappedClass = 'file-preview-lightbox__text-content--wrapped'
    const wasWrapped = this.isTextWrapped()

    if (wasWrapped) {
      textElement.classList.remove(wrappedClass)
    }

    try {
      this.hasUnwrappedTextOverflow.set(textElement.scrollWidth > container.clientWidth + 1)
    } finally {
      if (wasWrapped && this.isTextWrapped()) {
        textElement.classList.add(wrappedClass)
      }
    }
  }

  /**
   * Publishes one renderer URL and its sanitized iframe variant.
   * @param {string} url Browser-accessible media URL.
   */
  private setPreviewUrl(url: string): void {
    this.previewUrl.set(url)
    this.safePreviewResourceUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url))
    this.isLoading.set(this.currentFile()?.previewKind === 'image')
  }

  /**
   * Decodes the rendered image before fitting and revealing it.
   * @param {Function} onCleanup Registers cancellation when the source changes or the lightbox closes.
   */
  private decodeImagePreview(onCleanup: (cleanup: () => void) => void): void {
    const image = this.previewImage()?.nativeElement
    const url = this.previewUrl()

    if (!image || !url) {
      return
    }

    const subscription = from(image.decode()).subscribe({
      next: () => {
        if (this.previewUrl() !== url) {
          return
        }

        this.imageDimensions.set(
          this.currentFile()?.imageDimensions ?? { width: image.naturalWidth, height: image.naturalHeight }
        )
        this.scaleToFit()
        this.isLoading.set(false)
      },
      error: () => {
        if (this.previewUrl() === url) {
          this.onPreviewFailed()
        }
      }
    })

    onCleanup(() => subscription.unsubscribe())
  }

  /**
   * Updates fitted zoom from the selected image dimensions.
   */
  private updateFitScale(): void {
    const dimensions = this.imageDimensions()
    this.fitScale.set(this.zoomService.calculateFitScale(dimensions?.width, dimensions?.height))
  }

  /**
   * Applies one pointer delta to the image pan offset.
   * @param {number} clientX Current pointer x coordinate.
   * @param {number} clientY Current pointer y coordinate.
   */
  private panBy(clientX: number, clientY: number): void {
    this.zoomService.updatePanOffset(clientX - this.lastPanPosition.x, clientY - this.lastPanPosition.y)
    this.lastPanPosition = { x: clientX, y: clientY }
    this.constrainPanOffset()
  }

  /**
   * Keeps the selected image within its visible viewport.
   */
  private constrainPanOffset(): void {
    const dimensions = this.imageDimensions()
    const container = this.imagePreview()?.nativeElement.parentElement

    if (!dimensions || !container) {
      return
    }

    this.zoomService.constrainPanOffset(
      dimensions.width,
      dimensions.height,
      container.clientWidth,
      container.clientHeight
    )
  }

  /**
   * Keeps the selected thumbnail visible in an overflowing gallery.
   */
  private scrollSelectedThumbnailIntoView(): void {
    const thumbnail = this.thumbnailsDetails()?.nativeElement.querySelectorAll<HTMLElement>(
      '.file-preview-lightbox__thumbnail'
    )[this.selectedIndex()]
    thumbnail?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
  }

  /**
   * Prevents a cancelable touch event from scrolling the page during panning.
   * @param {TouchEvent} event Touch event to suppress.
   */
  private preventTouchDefault(event: TouchEvent): void {
    if (event.cancelable) {
      event.preventDefault()
    }
  }

  /**
   * Captures Escape before an underlying popup handles the same event.
   * @param {KeyboardEvent} event Document keydown event.
   */
  private handleCapturedEscape(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || this.modalService.modals.length > this.modalStackDepthAtOpen) {
      return
    }

    event.preventDefault()
    event.stopImmediatePropagation()
    this.close()
  }

  /**
   * Emits the close event once and restores focus to the opening control.
   */
  private finishClosing(): void {
    if (this.hasClosed) {
      return
    }

    this.hasClosed = true

    const origin = this.request().origin

    this.hiding.emit()
    queueMicrotask(() => {
      if (origin?.isConnected) {
        origin.focus({ preventScroll: true })
      }
    })
  }

  /**
   * Revokes the temporary URL owned by this lightbox.
   */
  private releaseOwnedPreviewUrl(): void {
    if (!this.ownedPreviewUrl) {
      return
    }

    URL.revokeObjectURL(this.ownedPreviewUrl)
    this.ownedPreviewUrl = null
  }
}
